import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteWhitelistedStudyFile,
  getWhitelistedStudyFiles,
  getWhitelistedStudyFileVersions,
  uploadWhitelistedStudyFile,
} from './whitelistedStudyFilesApi';
import type {
  WhitelistedStudyFilesFilters,
  WhitelistedStudyFileVersionsResponse,
} from '@/types/whitelistedStudyFiles';

// Query keys
export const WHITELISTED_STUDY_FILES_QUERY_KEY = 'whitelistedStudyFiles';
export const WHITELISTED_STUDY_FILE_VERSIONS_QUERY_KEY =
  'whitelistedStudyFileVersions';

/**
 * Hook to fetch whitelisted study files
 * Note: Created for API completeness but not actively used in UI
 * as per business requirements - file-upload section doesn't show uploaded files
 */
export const useWhitelistedStudyFiles = (
  filters?: WhitelistedStudyFilesFilters,
  enabled = true
) => {
  return useQuery({
    queryKey: [WHITELISTED_STUDY_FILES_QUERY_KEY, filters],
    queryFn: () => getWhitelistedStudyFiles(filters),
    enabled,
    staleTime: 30000, // 30 seconds
    select: data => ({
      files: data.data,
      pagination: data.pagination,
    }),
  });
};

/**
 * Hook to upload a new whitelisted study file
 */
export const useUploadWhitelistedStudyFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadWhitelistedStudyFile,
    onSuccess: () => {
      // Invalidate and refetch whitelisted study files
      queryClient.invalidateQueries({
        queryKey: [WHITELISTED_STUDY_FILES_QUERY_KEY],
      });
      // Also invalidate study files as they might be related
      queryClient.invalidateQueries({ queryKey: ['studyFiles'] });
    },
  });
};

/**
 * Hook to delete a whitelisted study file
 */
export const useDeleteWhitelistedStudyFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWhitelistedStudyFile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [WHITELISTED_STUDY_FILES_QUERY_KEY],
      });
      queryClient.invalidateQueries({ queryKey: ['studyFiles'] });
    },
  });
};

/**
 * Hook to fetch whitelisted study file versions
 */
export const useWhitelistedStudyFileVersions = (
  fileId: number,
  enabled = false
) => {
  return useQuery({
    queryKey: [WHITELISTED_STUDY_FILE_VERSIONS_QUERY_KEY, fileId],
    queryFn: () => getWhitelistedStudyFileVersions(fileId),
    enabled,
    select: (data: WhitelistedStudyFileVersionsResponse) => data.data,
  });
};
